import { useMemo } from 'react';
import type { ComponentProps, ComponentType } from 'react';
import {
  Box,
  ColumnLayout,
  Container,
  Header,
  Icon,
  LineChart,
  Popover,
  SpaceBetween,
  Spinner,
  StatusIndicator,
} from '@cloudscape-design/components';
import type { StatusIndicatorProps } from '@cloudscape-design/components';
import { useTodaySummary } from '../hooks/useTodaySummary';
import { usePageTitle } from '../lib/usePageTitle';
import { makeSegmentRenderable, pickBucketMs, toResampledLineSegments } from '../lib/energyChartSeries';
import { fmtDateTime, fmtEnergyWh, fmtPowerW } from '../format';
import type { DerivedMetricPoint, FreshnessStatus, SourceKind } from '../api/contracts';

/**
 * One entry per metric plotted on "Today's trend". Single source of truth
 * for both the chart's line segments AND the custom legend below it --
 * Cloudscape's own LineChart legend renders one swatch per SEGMENT (not
 * per metric), with no prop to hide a segment or the whole legend from its
 * public types, so a metric with several gap-broken segments produces
 * several blank-label swatches under one label (confirmed live, see PR
 * history). `hideLegend` IS implemented at runtime
 * (line-chart/index.js forwards it) despite being absent from
 * LineChartProps' published .d.ts -- passing it via the narrow cast below
 * suppresses Cloudscape's legend entirely so this list can render our own,
 * exactly 4 rows, unaffected by how many segments a sparse metric has.
 */
const CHART_METRICS: { key: keyof DerivedMetricPoint; title: string; color: string; filledKey?: keyof DerivedMetricPoint }[] = [
  { key: 'totalConsumptionW', title: 'Total consumption', color: '#1f77b4', filledKey: 'shp2Filled' },
  { key: 'gridImportW', title: 'Grid import', color: '#d62728' },
  { key: 'pvW', title: 'Solar generation', color: '#2ca02c', filledKey: 'pvFilled' },
  { key: 'batteryPowerW', title: 'Battery power', color: '#ff7f0e', filledKey: 'batteryFilled' },
];

function ChartLegendSwatch({ color }: { color: string }) {
  return (
    <span
      aria-hidden="true"
      style={{ display: 'inline-block', width: 16, height: 3, backgroundColor: color, borderRadius: 2 }}
    />
  );
}

function CustomChartLegend() {
  return (
    <SpaceBetween size="l" direction="horizontal">
      {CHART_METRICS.map((m) => (
        <span key={m.key} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <ChartLegendSwatch color={m.color} />
          <Box variant="small">{m.title}</Box>
        </span>
      ))}
    </SpaceBetween>
  );
}

/**
 * `hideLegend` is real, implemented Cloudscape behavior (line-chart/index.js
 * forwards `props.hideLegend` straight into the internal chart), just not
 * declared on the public `LineChartProps` type -- this narrow cast adds
 * exactly that one prop back rather than casting the whole component to
 * `any`, so everything else on `<LineChart>` stays fully type-checked.
 */
type LineChartPropsWithHideLegend = ComponentProps<typeof LineChart> & { hideLegend?: boolean };
const LineChartWithHideLegend = LineChart as unknown as ComponentType<LineChartPropsWithHideLegend>;

const FRESHNESS_INDICATOR: Record<FreshnessStatus, StatusIndicatorProps.Type> = {
  fresh: 'success',
  stale: 'warning',
  never: 'error',
};

const SOURCE_LABEL: Record<SourceKind, string> = {
  emporia: 'Emporia (main panel)',
  ecoflow_shp2: 'EcoFlow SHP2 (sub-panel)',
  ecoflow_dpu: 'EcoFlow DPU (solar + battery)',
  georgia_power: 'Georgia Power (utility)',
};

/**
 * Definitions for the four headline tiles, matching db/types.ts's
 * DerivedMetric formulas and docs/PLAN.md's energy model exactly -- these
 * are NOT a flat sum of circuits, and the wording here should stay in sync
 * with those two sources if the formulas ever change.
 */
const TILE_DEFINITIONS: Record<'total' | 'grid' | 'solarBattery' | 'unmonitored', string> = {
  total:
    'Everything the house used: grid import plus whatever the sub-panel got from solar/battery instead of ' +
    'the grid. total = gridImport + solarBatteryServed -- never computed by summing individual circuits.',
  grid:
    "Power drawn from the utility grid, measured at the main panel's service-entrance CTs. This already " +
    "includes the sub-panel's own grid draw, since the sub-panel is fed through an unmetered feeder off " +
    'the main panel rather than its own utility connection.',
  solarBattery:
    "The sub-panel's load that solar/battery covered instead of the grid. solarBatteryServed = " +
    "sub-panel's total load - the sub-panel's own grid draw. This site's solar can only serve the " +
    'sub-panel and never back-feeds the grid, so this number is always 0 or positive.',
  unmonitored:
    'Main-panel usage not captured by any individual circuit sensor, after also subtracting the ' +
    "sub-panel's grid draw so that load isn't double-counted here as well as in its own tile. " +
    'unmonitored = mains - sum(branch circuits) - sub-panel grid draw.',
};

function TileHeader({ title, definition }: { title: string; definition: string }) {
  return (
    <Header
      variant="h3"
      info={
        <Popover
          triggerType="custom"
          content={definition}
          dismissButton={false}
          size="medium"
        >
          <Box display="inline-block" padding={{ left: 'xxs' }}>
            <Icon name="status-info" variant="subtle" size="small" ariaLabel={`What is "${title}"?`} />
          </Box>
        </Popover>
      }
    >
      {title}
    </Header>
  );
}

export default function Home() {
  usePageTitle('Overview');
  const { data, isLoading, isPending } = useTodaySummary();

  const chartSeries = useMemo(() => {
    if (!data || data.series.length === 0) return [];
    const first = new Date(data.series[0]!.ts).getTime();
    const last = new Date(data.series[data.series.length - 1]!.ts).getTime();
    const bucketMs = pickBucketMs(Math.max(last - first, 60_000));
    return CHART_METRICS.flatMap((m) =>
      toResampledLineSegments(
        data.series,
        (p) => p[m.key] as number | undefined,
        m.title,
        bucketMs,
        m.color,
        m.filledKey ? (p) => Boolean(p[m.filledKey!]) : undefined,
      ),
    ).map((segment) => ({ ...segment, data: makeSegmentRenderable(segment.data) }));
  }, [data]);

  if (isLoading || isPending) {
    return (
      <SpaceBetween size="l">
        <Header variant="h1">Overview</Header>
        <Container>
          <Box textAlign="center" padding="l">
            <Spinner size="large" />
            <Box variant="p" color="text-body-secondary" padding={{ top: 's' }}>
              Loading today's energy summary…
            </Box>
          </Box>
        </Container>
      </SpaceBetween>
    );
  }

  if (!data) {
    // Errors are surfaced by the shared NotificationProvider toast (see
    // useTodaySummary). This is the quiet fallback for that same failed/empty
    // state so the page doesn't also render a second, competing error panel.
    return (
      <SpaceBetween size="l">
        <Header variant="h1">Overview</Header>
        <Container>
          <Box textAlign="center" color="text-body-secondary" padding="l">
            No data available right now.
          </Box>
        </Container>
      </SpaceBetween>
    );
  }

  const { totals, latest, freshness } = data;

  return (
    <SpaceBetween size="l">
      <Header
        variant="h1"
        description={latest ? `As of ${fmtDateTime(latest.ts)}` : 'No readings yet today'}
      >
        Overview
      </Header>

      <ColumnLayout columns={4} variant="text-grid">
        <Container header={<TileHeader title="Total consumption" definition={TILE_DEFINITIONS.total} />}>
          <Box variant="awsui-value-large">{fmtEnergyWh(totals.totalConsumptionWh)}</Box>
        </Container>
        <Container header={<TileHeader title="Grid import" definition={TILE_DEFINITIONS.grid} />}>
          <Box variant="awsui-value-large">{fmtEnergyWh(totals.gridImportWh)}</Box>
        </Container>
        <Container header={<TileHeader title="Solar + battery served" definition={TILE_DEFINITIONS.solarBattery} />}>
          <Box variant="awsui-value-large">{fmtEnergyWh(totals.solarBatteryServedWh)}</Box>
        </Container>
        <Container header={<TileHeader title="Unmonitored main load" definition={TILE_DEFINITIONS.unmonitored} />}>
          <Box variant="awsui-value-large">{fmtEnergyWh(totals.unmonitoredMainWh)}</Box>
        </Container>
      </ColumnLayout>

      <Container header={<Header variant="h2">Today's trend</Header>}>
        <div data-testid="today-trend">
          <LineChartWithHideLegend
            series={chartSeries}
            xScaleType="time"
            xTitle="Time"
            yTitle="Watts"
            height={300}
            hideFilter
            hideLegend
            yTickFormatter={(v) => fmtPowerW(v)}
            xTickFormatter={(v) => fmtDateTime(v)}
            detailPopoverSeriesContent={({ series, y }) => ({
              key: series.title,
              value: fmtPowerW(y),
            })}
            empty={
              <Box textAlign="center" color="inherit">
                No readings yet today
              </Box>
            }
            ariaLabel="Today's consumption, grid import, solar generation, and battery power trend"
          />
          <Box padding={{ top: 's' }}>
            <CustomChartLegend />
          </Box>
          <Box variant="small" color="text-body-secondary" padding={{ top: 's' }}>
            Gaps in a line mean a reading was missing for that minute — never rendered as a dip to
            zero. A lighter tint means the solar/battery reading was carried forward from EcoFlow's
            last poll (it reports roughly every few minutes, vs. every minute for grid import) rather
            than measured that exact minute. Battery power is signed: positive means discharging
            (providing power), negative means charging. Solar generation is raw production,
            independent of whether it's currently charging the battery or also supplying the house.
          </Box>
        </div>
      </Container>

      <Container header={<Header variant="h2">System status</Header>}>
        <ColumnLayout columns={freshness.length || 1} variant="text-grid">
          {freshness.map((f) => (
            <div key={f.sourceKind}>
              <Box variant="awsui-key-label">{SOURCE_LABEL[f.sourceKind] ?? f.sourceKind}</Box>
              <StatusIndicator type={FRESHNESS_INDICATOR[f.status]}>{f.status}</StatusIndicator>
              <Box variant="small" color="text-body-secondary">
                Last run: {fmtDateTime(f.lastRunAt)}
                {f.lastRunStatus ? ` (${f.lastRunStatus})` : ''}
              </Box>
            </div>
          ))}
        </ColumnLayout>
      </Container>
    </SpaceBetween>
  );
}
